/**
 * [REDE] Mapa de uma unidade na tela de escolha — com a LOJA e VOCÊ juntos.
 *
 * Memória: `.claude/.../memory/project_rede_cardapio_por_unidade.md`
 *
 * Dois pinos e enquadramento automático nos dois é a única forma de responder
 * "qual está mais perto de mim?" com o olho, não com o número (pedido do dono).
 *
 * Mapa ABERTO (Leaflet + OpenStreetMap) desde 30/09/2026: antes era o Maps JS
 * do Google com a chave da loja entregue por um endpoint público — a chave
 * chegava a qualquer pessoa (auditoria 29/09). Carrega sob demanda (só quando
 * o cliente toca em "Ver no mapa"). Se o mapa não carregar, cai no iframe do
 * Google (sem chave): perde o pino do cliente, mantém arrastar e dar zoom.
 */
import { useEffect, useRef, useState } from 'react';
import { loadLeaflet, criarMapa, iconeHtml } from '../../../shared/leafletLoader';
import { t } from '../i18n';

interface LatLng { lat: number; lng: number }

const PIN_LOJA = '<div style="width:34px;height:34px;display:flex;align-items:center;justify-content:center;color:#ef4444;font-size:30px;filter:drop-shadow(0 2px 2px rgba(0,0,0,.35));transform:translateY(-14px)"><i class="fas fa-location-dot"></i></div>';
const PIN_VOCE = '<div style="width:16px;height:16px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 0 0 2px rgba(37,99,235,.35)"></div>';

export function MapaUnidade({
  lat, lng, nome, cliente,
}: {
  lat: number;
  lng: number;
  nome: string;
  /** Posição do cliente, quando ele permitiu a localização. */
  cliente: LatLng | null;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [estado, setEstado] = useState<'carregando' | 'ok' | 'sem-mapa'>('carregando');

  useEffect(() => {
    let cancelado = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let map: any = null;
    loadLeaflet()
      .then((L) => {
        if (cancelado || !boxRef.current) return;
        map = criarMapa(L, boxRef.current, { zoomControl: true });
        map.setView([lat, lng], 16);
        L.marker([lat, lng], { icon: iconeHtml(L, PIN_LOJA, 34), title: nome }).addTo(map);
        if (cliente) {
          L.marker([cliente.lat, cliente.lng], { icon: iconeHtml(L, PIN_VOCE, 16), title: t('Você está aqui'), zIndexOffset: 999 }).addTo(map);
          // Enquadra os dois; `maxZoom` evita o zoom absurdo de quando os
          // pontos estão a poucos metros (caso real: duas unidades a 23 m).
          map.fitBounds(L.latLngBounds([[lat, lng], [cliente.lat, cliente.lng]]), { padding: [40, 40], maxZoom: 16 });
        }
        setEstado('ok');
      })
      .catch(() => { if (!cancelado) setEstado('sem-mapa'); });
    return () => {
      cancelado = true;
      if (map) { try { map.remove(); } catch { /* */ } }
    };
  }, [lat, lng, nome, cliente?.lat, cliente?.lng]);

  if (estado === 'sem-mapa') {
    return (
      <div className="rounded-xl overflow-hidden border border-gray-200">
        <iframe
          title={t('Mapa da loja')}
          width="100%"
          height={240}
          style={{ border: 0 }}
          loading="lazy"
          src={`https://maps.google.com/maps?q=${lat},${lng}&hl=pt-br&z=16&output=embed`}
        />
      </div>
    );
  }

  return (
    <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-100">
      <div ref={boxRef} style={{ width: '100%', height: 240 }} />
      {estado === 'carregando' && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-400">
          <i className="fas fa-circle-notch fa-spin" />
        </div>
      )}
      {estado === 'ok' && cliente && (
        <div className="absolute top-2 right-2 z-[500] bg-white/95 backdrop-blur px-2.5 py-1.5 rounded-lg shadow text-[10px] font-bold text-gray-600 flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-600 border-2 border-white shadow-sm" />
            {t('Você')}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="fas fa-location-dot text-red-500" />
            {t('A loja')}
          </span>
        </div>
      )}
    </div>
  );
}
